import { readonly, shallowRef, watch } from 'vue'

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const

export type ThemePreference = typeof THEME_PREFERENCES[number]

interface ThemePreferenceSource {
  preference: string
}

export function normalizeThemePreference(value: unknown): ThemePreference {
  return THEME_PREFERENCES.includes(value as ThemePreference)
    ? value as ThemePreference
    : 'system'
}

export function themePreferenceIcon(preference: ThemePreference): string {
  return {
    light: 'i-lucide-sun',
    dark: 'i-lucide-moon',
    system: 'i-lucide-monitor-cog'
  }[preference]
}

/**
 * Shared source of truth for public, customer and staff theme controls.
 *
 * Nuxt Color Mode owns persistence and follows `prefers-color-scheme` while
 * the preference is `system`; product shells only select the preference.
 */
export function createThemePreferenceController(
  colorMode: ThemePreferenceSource
) {
  const preference = shallowRef<ThemePreference>(
    normalizeThemePreference(colorMode.preference)
  )

  watch(
    () => colorMode.preference,
    value => {
      preference.value = normalizeThemePreference(value)
    },
    { immediate: true }
  )

  function setThemePreference(nextPreference: ThemePreference) {
    preference.value = nextPreference
    colorMode.preference = nextPreference
  }

  return {
    preference: readonly(preference),
    setThemePreference
  }
}

export function useThemePreference() {
  return createThemePreferenceController(useColorMode())
}
